import { useCallback, useEffect, useRef } from 'react';
import type { ChangeEvent } from 'react';
import { DEFAULT_IMAGE_LIBRARY_PAGE_SIZE, listImageLibraryItems } from '../api/imageLibraryApi';
import type { ImageLibraryItem } from '../api/imageLibraryApi';
import type { LibraryPanelState } from './libraryPanelTypes';
import type { BrowseSubTab, SelectedImageData } from './imageInsertTypes';

const DEBOUNCE_DELAY = 600;

interface UseLibraryPanelControllerProps {
  state: LibraryPanelState;
  onStateChange: (state: LibraryPanelState) => void;
  onImageReady: (data: SelectedImageData) => void;
}

export function useLibraryPanelController({ state, onStateChange, onImageReady }: UseLibraryPanelControllerProps) {
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const loadedScopeRef = useRef<number | null | undefined>(undefined);
  const stateRef = useRef(state);
  stateRef.current = state;
  const requestIdRef = useRef(0);
  const abortControllerRef = useRef<AbortController | null>(null);

  const commitState = useCallback((nextState: LibraryPanelState) => {
    stateRef.current = nextState;
    onStateChange(nextState);
  }, [onStateChange]);

  const cancelPending = useCallback(() => {
    if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    debounceTimerRef.current = null;
    abortControllerRef.current?.abort();
    requestIdRef.current += 1;
  }, []);

  const fetchImages = useCallback(async (
    query: string,
    tab: BrowseSubTab = stateRef.current.browseSubTab,
    page = stateRef.current.page,
    append = false,
    recovering = false,
  ): Promise<boolean | undefined> => {
    const pageSize = DEFAULT_IMAGE_LIBRARY_PAGE_SIZE;
    cancelPending();
    const requestId = requestIdRef.current;
    const accountId = stateRef.current.accountId;
    const committedQuery = query.trim();
    const abortController = new AbortController();
    abortControllerRef.current = abortController;
    // State preserves request history when the selection screen remounts.
    const isInitialLoad = !stateRef.current.hasRequested;
    commitState({
      ...stateRef.current, browseSubTab: tab, page: append ? stateRef.current.page : page, pageSize, committedQuery,
      images: append ? stateRef.current.images : [], totalCount: append ? stateRef.current.totalCount : 0,
      selectedImage: append ? stateRef.current.selectedImage : null, isLoading: !append, isLoadingMore: append,
      isInitialLoad, hasRequested: true, error: null, appendError: null,
      recoveryAttempted: append ? stateRef.current.recoveryAttempted : recovering,
    });
    const isCurrent = () => {
      const current = stateRef.current;
      return !abortController.signal.aborted && requestId === requestIdRef.current
        && current.accountId === accountId && current.browseSubTab === tab
        && current.committedQuery === committedQuery;
    };
    try {
      const result = tab === 'saved' && accountId === null
        ? { images: [], totalCount: 0 }
        : await listImageLibraryItems({
          externalAccountId: tab === 'saved' ? accountId : null,
          query: committedQuery, page, pageSize, signal: abortController.signal,
        });
      if (!isCurrent()) return;
      const images = [...new Map((append ? [...stateRef.current.images, ...result.images] : result.images)
        .map((image) => [image.id, image])).values()];
      if (images.length > result.totalCount || (images.length < result.totalCount
        && (!result.images.length || page * pageSize >= result.totalCount))) {
        if (!stateRef.current.recoveryAttempted) return fetchImages(committedQuery, tab, 1, false, true);
        commitState({ ...stateRef.current, isLoading: false, isLoadingMore: false,
          images: [], selectedImage: null, error: 'Image results changed. Retry to refresh the library.' });
        return false;
      }
      commitState({
        ...stateRef.current, images, page, totalCount: result.totalCount,
        isLoading: false, isLoadingMore: false, isInitialLoad: false,
      });
      return images.length >= result.totalCount;
    } catch (error) {
      if (!isCurrent()) return;
      commitState({
        ...stateRef.current, [append ? 'appendError' : 'error']: error instanceof Error ? error.message : 'Failed to load images',
        isLoading: false, isLoadingMore: false, isInitialLoad: false,
      });
    }
    return false;
  }, [cancelPending, commitState]);

  useEffect(() => {
    if (loadedScopeRef.current !== state.accountId) {
      const isScopeChange = loadedScopeRef.current !== undefined;
      loadedScopeRef.current = state.accountId;
      // Back from image details reuses settled batches and their selection.
      // Aborted/loading requests still restart, including StrictMode replay.
      if (!isScopeChange && state.hasRequested && !state.isLoading) {
        if (state.isLoadingMore) commitState({ ...stateRef.current, isLoadingMore: false });
        return;
      }
      // Returning from image details keeps local paging; a different account starts at page 1.
      void fetchImages(state.committedQuery, state.browseSubTab, 1);
    }
  }, [commitState, fetchImages, state.accountId, state.browseSubTab, state.committedQuery, state.page, state.pageSize, state.hasRequested, state.isLoading, state.isLoadingMore, state.error]);

  useEffect(() => () => {
    cancelPending();
    // StrictMode replays mount effects after cleanup; its replacement request
    // must run, but must not be classified as the session's initial request.
    loadedScopeRef.current = undefined;
  }, [cancelPending]);

  const handleSearchChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    const searchQuery = event.target.value;
    cancelPending();
    commitState({
      ...stateRef.current, searchQuery, selectedImage: null, images: [], totalCount: 0,
      isLoading: true, isLoadingMore: false, appendError: null, isInitialLoad: false, error: null,
    });
    if (!searchQuery.trim()) {
      void fetchImages('', stateRef.current.browseSubTab, 1);
      return;
    }
    const accountId = stateRef.current.accountId;
    debounceTimerRef.current = setTimeout(() => {
      if (stateRef.current.accountId !== accountId || stateRef.current.searchQuery !== searchQuery) return;
      void fetchImages(searchQuery, stateRef.current.browseSubTab, 1);
    }, DEBOUNCE_DELAY);
  }, [cancelPending, commitState, fetchImages]);

  const canSelect = useCallback((image: ImageLibraryItem) => {
    const current = stateRef.current;
    return !current.isLoading && !current.error && current.searchQuery.trim() === current.committedQuery
      && current.images.some((item) => item.id === image.id);
  }, []);

  const handleImageSelect = useCallback((image: ImageLibraryItem) => {
    if (canSelect(image)) commitState({ ...stateRef.current, selectedImage: image });
  }, [canSelect, commitState]);

  const handleImageDoubleClick = useCallback((image: ImageLibraryItem) => {
    if (!canSelect(image)) return;
    commitState({ ...stateRef.current, selectedImage: image });
    onImageReady({
      previewUrl: image.publicUrl, needsUpload: false, suggestedAlt: image.altText,
      aiGeneratedAlt: false, mimeType: image.contentType, sourceType: 'library-browse',
      libraryItemId: image.id, libraryLabel: image.label, libraryFilename: image.originalFilename,
      s3Key: image.s3Key, storagePrefix: image.storagePrefix,
    });
  }, [canSelect, commitState, onImageReady]);

  const handleSubTabChange = useCallback((tab: BrowseSubTab) => {
    void fetchImages(stateRef.current.searchQuery, tab, 1);
  }, [fetchImages]);

  const handleLoadMore = useCallback(async () => {
    const current = stateRef.current;
    if (current.isLoading || current.isLoadingMore || current.error || current.images.length >= current.totalCount) return false;
    return fetchImages(current.committedQuery, current.browseSubTab, current.page + 1, true);
  }, [fetchImages]);

  const handleRetry = useCallback(() => {
    const current = stateRef.current;
    void fetchImages(current.committedQuery, current.browseSubTab, 1);
  }, [fetchImages]);

  const handleUseImage = useCallback(() => {
    if (stateRef.current.selectedImage) handleImageDoubleClick(stateRef.current.selectedImage);
  }, [handleImageDoubleClick]);

  return { handleSearchChange, handleImageSelect, handleImageDoubleClick, handleSubTabChange,
    handleLoadMore, handleRetry, handleUseImage };
}
